'use client';

import { Session } from 'next-auth';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';

import {
    Container,
    Row,
    Rows,
    Section,
    Title,
    TitleLevels,
    TitleMarkup,
    TrilogyColor,
    TypographyAlign,
} from '@bytel/trilogy-react';

import { AnimcoBannerType } from '@app-types/animco';
import { LegalType } from '@app-types/legals';
import { CategoryListType, CategoryPlanType, MobilePlanType } from '@app-types/plan';
import { Product } from '@app-types/product';
import { UserType } from '@app-types/user';

import { AnimcoBanner } from '@components/animco-banner';
import { Legals } from '@components/legals';
import { LetterVariant } from '@components/letter-variant';
import { SSIBlock } from '@components/ssi-block';

import { MarketingsState, PartialMarketingsState } from '@hooks/use-marketings';

import { getCurrentPhone, getCurrentPhoneInCart } from '@helpers/cart';
import { replaceWindowUrl } from '@helpers/document';
import { partials } from '@helpers/partials';

import { API_X_VERSION } from '@constants/api';
import { MarketingType, PlanTypeId } from '@constants/plan';

import { getOffers } from '@home-mobile/actions';
import { OfferCategoryListing, OfferConfigurator, OfferWallTabs } from '@home-mobile/components';
import { OfferWallViewTracking, getQuotePayload } from '@home-mobile/helpers';
import { useInitPhoneInCart } from '@home-mobile/hooks';

import { ConnectionIncentive } from '../connection-incentive';

type OfferWallAcquisitionContainerProps = {
    categoryList: CategoryListType;
    categoryListToDisplay: CategoryPlanType[];
    marketingDiscounts: MarketingsState;
    setMarketingDiscounts: (marketings: PartialMarketingsState) => MarketingsState;
    isWithSmartphone: boolean;
    openOfferDetails: (offer: MobilePlanType) => void;
    phoneInCart?: Product;
    setPhoneInCart: (phone?: Product) => void;
    onAddToCart: (chosenOffer: MobilePlanType, fundingKey?: string) => Promise<void>;
    animco: AnimcoBannerType[];
    session: Session | null;
    canToggleClientMobile: boolean;
};

export function OfferWallAcquisitionContainer({
    categoryList,
    categoryListToDisplay: _categoryListToDisplay,
    isWithSmartphone,
    openOfferDetails,
    setMarketingDiscounts,
    marketingDiscounts,
    phoneInCart,
    setPhoneInCart,
    onAddToCart,
    animco,
    session,
    canToggleClientMobile,
}: Readonly<OfferWallAcquisitionContainerProps>) {
    const isGPCategory = isWithSmartphone ? PlanTypeId.PREMIUM : PlanTypeId.SIMO;
    const [currentCategory, setCurrentCategory] = useState<PlanTypeId>(isGPCategory);
    const [categoryListToDisplay, setCategoryListToDisplay] = useState<CategoryPlanType[]>(_categoryListToDisplay);
    const [allCategoryList, setAllCategoryList] = useState<CategoryPlanType[]>(categoryList);
    const router = useRouter();
    const pathname = usePathname();
    const existingSearchParams = useSearchParams();

    const toggleValues = useMemo(
        () => ({
            clientBbox: marketingDiscounts.FOYER,
            clientMobile: canToggleClientMobile ? marketingDiscounts.MOBILE : false,
        }),
        [marketingDiscounts],
    );

    useInitPhoneInCart(
        setAllCategoryList,
        setCategoryListToDisplay,
        filterCategories,
        currentCategory,
        session,
        toggleValues,
    );

    function switchCategory(isPremium: boolean = true) {
        const newCategory = isPremium ? PlanTypeId.PREMIUM : PlanTypeId.SIMO;
        setCurrentCategory(newCategory);
        const filteredCategories = filterCategories(newCategory, allCategoryList);
        setCategoryListToDisplay(filteredCategories);

        const params = new URLSearchParams(existingSearchParams);
        params.set('smartphone', isPremium.toString());
        router.replace(`${pathname}?${params.toString()}`, { scroll: false });
    }

    const onChangeCategory = (switchCategoryValue: boolean) => {
        switchCategory(switchCategoryValue);
    };

    function filterCategories(categoryCode: string, list: CategoryListType) {
        return list?.filter((category: CategoryPlanType) => category?.code.includes(categoryCode));
    }

    async function onChangePromos({ clientBbox, clientMobile }: { clientBbox?: boolean; clientMobile?: boolean }) {
        if (session?.user) {
            return;
        }

        const getDiscountValue = (isFeatureEnabled: boolean, clientValue?: boolean, currentValue?: boolean) =>
            isFeatureEnabled ? (clientValue ?? currentValue) : false;

        setMarketingDiscounts({
            ...marketingDiscounts,
            [MarketingType.HOME]: getDiscountValue(true, clientBbox, marketingDiscounts[MarketingType.HOME]),
            [MarketingType.MOBILE]: getDiscountValue(
                canToggleClientMobile,
                clientMobile,
                marketingDiscounts[MarketingType.MOBILE],
            ),
        });

        const apiVersion = phoneInCart?.gencode ? API_X_VERSION.V4 : API_X_VERSION.V5;

        const quotes = getQuotePayload(
            clientBbox,
            clientMobile && canToggleClientMobile,
            phoneInCart?.gencode,
            apiVersion,
        );

        const offers = await getOffers({ cart: { quotes } }, session, apiVersion);
        setAllCategoryList(offers);
        const filteredCategories = filterCategories(currentCategory, offers);
        setCategoryListToDisplay(filteredCategories);

        if (clientBbox !== undefined || clientMobile !== undefined) {
            const params = new URLSearchParams(existingSearchParams);
            params.set('clientbbox', clientBbox?.toString() ?? 'false');

            if (canToggleClientMobile) {
                params.set('clientmobile', clientMobile?.toString() ?? 'false');
            }
            replaceWindowUrl(`${pathname}?${params.toString()}${window.location.hash}`);
        }
        handleHashChange();
    }

    function handleHashChange() {
        // TODO : Temporary fix while figuring out why categories are changing on first render (https://local.bouyguestelecom.fr/forfaits-mobiles/avec-engagement?clientbbox=true&smartphone=false)
        setTimeout(() => {
            const hash = window.location.hash;
            if (hash) {
                const element = document.querySelector(hash);
                if (element) {
                    element.scrollIntoView();
                }
            }
        }, 50);
    }

    const minOfferEligible = useMemo(() => {
        return allCategoryList.flatMap((category) =>
            category.products
                ?.filter(
                    (product) =>
                        (product.details?.discounts?.filter(
                            (discount) =>
                                !discount.isIncitation && discount.marketingTypes.includes(MarketingType.HOME),
                        ).length || 0) > 0,
                )
                ?.sort((a, b) => (a.dataSize || 0) - (b.dataSize || 0)),
        )[0];
    }, [allCategoryList]);

    const smallPricePlans = categoryListToDisplay
        ?.filter(
            (category) =>
                category.code === PlanTypeId.PREMIUM_MULTILIGNE || category.code === PlanTypeId.SIMO_MULTILIGNE,
        )
        ?.flatMap((category) => category.products)
        .flatMap((product) => product.gencode);

    const hasAnyProducts = (data: CategoryListType, codes: string[]) =>
        codes.some((code) => {
            const product = data.find((item) => item.code.includes(code));
            return product?.products && product?.products.length > 0;
        });
    const hasSimoProducts = hasAnyProducts(categoryList, [PlanTypeId.SIMO]);

    // TODO: setPhoneInCart in the parent component to avoid code duplication (offer-wall-renewal-container.tsx)
    useEffect(() => {
        const simulatedPhone = getCurrentPhone(categoryListToDisplay?.[0]?.products[0]?.simulatedCart);
        if (simulatedPhone) {
            getCurrentPhoneInCart().then((cartPhone) => {
                setPhoneInCart({ ...cartPhone, ...simulatedPhone });
            });
        }
    }, [categoryListToDisplay]);

    useEffect(() => {
        OfferWallViewTracking(false, isWithSmartphone, false, !!session?.user, marketingDiscounts.FOYER);
    }, [session?.user?.sub, isWithSmartphone]);

    return (
        <>
            <AnimcoBanner banner={animco[0]} />
            <Section backgroundColor={TrilogyColor.NEUTRAL_FADE}>
                <Container>
                    <Rows>
                        <Row>
                            <Title markup={TitleMarkup.H1} level={TitleLevels.ONE} typo={TypographyAlign.TEXT_CENTERED}>
                                Les nouveaux forfaits m<LetterVariant letter='o' />
                                bile Bouygues Telecom
                            </Title>
                            <SSIBlock tagId='offer-wall-top' url={partials.MARKETING.OFFER_WALL_SLIDER} />
                        </Row>
                        <Row>
                            <ConnectionIncentive user={session?.user as UserType} minOfferEligible={minOfferEligible} />
                        </Row>

                        {!session && (
                            <Row>
                                <OfferConfigurator
                                    onChangePromos={onChangePromos}
                                    isClientBbox={marketingDiscounts[MarketingType.HOME]}
                                    isClientMobile={marketingDiscounts[MarketingType.MOBILE]}
                                    canToggleClientMobile={canToggleClientMobile}
                                />
                            </Row>
                        )}
                        <Row>
                            <OfferWallTabs
                                onChangeCategory={onChangeCategory}
                                currentCategory={currentCategory}
                                hasSimoProducts={hasSimoProducts}
                            />
                        </Row>
                        <Row>
                            <Rows gap={10}>
                                {categoryListToDisplay.map((category: CategoryPlanType, index: number) => (
                                    <OfferCategoryListing
                                        key={category.id}
                                        category={category}
                                        canBePartiallyHidden={index === 1}
                                        openOfferDetails={openOfferDetails}
                                        currentCategory={currentCategory}
                                        phoneInCart={phoneInCart}
                                        onAddToCart={onAddToCart}
                                        isConnected={!!session?.user}
                                        marketingDiscounts={marketingDiscounts}
                                        isSmallPriceCategory={
                                            category.code === PlanTypeId.PREMIUM_MULTILIGNE ||
                                            category.code === PlanTypeId.SIMO_MULTILIGNE
                                        }
                                        smallPricePlanGencodes={smallPricePlans}
                                    />
                                ))}
                            </Rows>
                        </Row>
                    </Rows>
                </Container>
            </Section>
            <Section backgroundColor={TrilogyColor.BACKGROUND}>
                <Container>
                    <SSIBlock tagId='fai-contenu' url={partials.MARKETING.FAI_CONTENT} />
                    <SSIBlock tagId='offer-wall-bottom' url={partials.MARKETING.FAI_BOTTOM_CONTENT} />
                </Container>
            </Section>
            <Section backgroundColor={TrilogyColor.NEUTRAL_FADE}>
                <Container>
                    <Legals id='offer-legals' url={LegalType.OFFER_WALL} />
                </Container>
            </Section>
        </>
    );
}
